import { Link } from "react-router-dom";
import {
  ArrowLeft,
  IdCard,
  Info,
  PlusCircle,
  QrCode,
  Search,
  UserRound,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass } from "../components/FormField";
import { StatusBadge } from "../components/StatusBadge";
import { useCreateHospitalAdmission } from "../hooks/useCreateHospitalAdmission";
import { cn } from "@/shared/lib/cn";

export function CreateAdmissionPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const labels = t.safecheck.admissions.create;
  const patientTypes = t.safecheck.admissions.patientTypes;

  const {
    systemId,
    setSystemId,
    results,
    hasSearched,
    selectedPatientId,
    setSelectedPatientId,
    selected,
    dateAdmission,
    setDateAdmission,
    motif,
    setMotif,
    searching,
    submitting,
    error,
    usingApi,
    search,
    submit,
  } = useCreateHospitalAdmission();

  const handleScanQr = () => {
    showToast({ message: labels.scanUnavailable, variant: "warning" });
  };

  const handleSubmit = async () => {
    const ok = await submit();
    if (ok && !usingApi) {
      showToast({ message: t.actions.saved, variant: "success" });
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <Breadcrumbs
          homeHref="/dashboard/hospital"
          items={[
            { label: t.safecheck.nav.admissions, href: "/dashboard/admissions" },
            { label: t.safecheck.nav.newAdmission },
          ]}
        />
        <Link
          to="/dashboard/admissions"
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
        >
          <ArrowLeft size={16} />
          {labels.backToList}
        </Link>
      </div>

      <div className="theme-card overflow-hidden">
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft">
            <PlusCircle size={20} className="text-primary" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">{labels.pageTitle}</h1>
        </div>

        <div className="border-b border-border bg-primary-soft/40 px-5 py-4">
          <p className="flex items-start gap-2 text-sm text-foreground">
            <Info size={16} className="mt-0.5 shrink-0 text-primary" />
            <span>{labels.instructions}</span>
          </p>
          {usingApi && (
            <p className="mt-2 text-xs text-muted">Données synchronisées depuis l&apos;API.</p>
          )}
          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5 flex items-center gap-2 border-b border-border pb-4">
            <IdCard size={18} className="text-purple" />
            <h2 className="text-sm font-semibold text-purple">{labels.step1Title}</h2>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr] lg:items-start">
            <div>
              <FormField
                label={labels.systemIdLabel}
                required
                hint={labels.systemIdHint}
              >
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    value={systemId}
                    onChange={(event) => setSystemId(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void search();
                    }}
                    placeholder={labels.systemIdPlaceholder}
                    className={inputClass}
                    disabled={searching || submitting}
                  />
                  <button
                    type="button"
                    onClick={() => void search()}
                    disabled={searching || submitting}
                    className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-primary px-5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
                  >
                    <Search size={16} />
                    {searching ? "…" : labels.search}
                  </button>
                </div>
              </FormField>
            </div>

            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {labels.scanQrLabel}
              </p>
              <button
                type="button"
                onClick={handleScanQr}
                className="flex h-[88px] w-full items-center justify-center gap-3 rounded-xl border border-border bg-card text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-card-muted"
              >
                <QrCode size={22} className="text-primary" />
                {labels.scanQrButton}
              </button>
            </div>
          </div>

          {hasSearched && (
            <div className="mt-8 border-t border-border pt-6">
              <p className="mb-4 text-sm font-medium text-foreground">{labels.selectPatient}</p>
              {results.length === 0 ? (
                <p className="text-sm text-muted">{labels.noResults}</p>
              ) : (
                <div className="space-y-3">
                  {results.map((patient) => {
                    const isSelected = selectedPatientId === patient.id;
                    return (
                      <button
                        key={patient.id}
                        type="button"
                        onClick={() => setSelectedPatientId(patient.id)}
                        disabled={submitting}
                        className={cn(
                          "flex w-full items-center justify-between gap-4 rounded-xl border px-4 py-4 text-left transition-colors",
                          isSelected
                            ? "border-primary bg-primary-soft/40"
                            : "border-border bg-card hover:border-primary/30 hover:bg-card-muted",
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-card-muted">
                            <UserRound size={18} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{patient.name}</p>
                            <p className="mt-0.5 text-xs text-muted">
                              {patient.systemId}
                              {patient.subtitle ? ` · ${patient.subtitle}` : ""}
                            </p>
                          </div>
                        </div>
                        <StatusBadge variant={patient.type === "employee" ? "info" : "purple"}>
                          {patientTypes[patient.type]}
                        </StatusBadge>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selected && (
            <div className="mt-8 border-t border-border pt-6">
              <h2 className="mb-4 text-sm font-semibold text-foreground">{labels.step2Title}</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label={labels.dateLabel} required>
                  <input
                    type="date"
                    value={dateAdmission}
                    onChange={(event) => setDateAdmission(event.target.value)}
                    className={inputClass}
                    disabled={submitting}
                  />
                </FormField>
                <FormField label={labels.motifLabel}>
                  <input
                    value={motif}
                    onChange={(event) => setMotif(event.target.value)}
                    placeholder={labels.motifPlaceholder}
                    className={inputClass}
                    disabled={submitting}
                  />
                </FormField>
              </div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={submitting}
                  className="rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-60"
                >
                  {submitting ? "Création…" : labels.submit}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
